import {initKiteSpots} from './layers/kite-spots.js';
// Entry point v2 — boot mappa + shell. I moduli layer si registrano qui.
import { createMap, setBase, applyMapTheme } from './mapcore.js';
import { readUrlState, STORAGE } from './lib/config.js';
import { createWorkspaceStore, TEMPORAL_MODES } from './core/workspace-store.js';
import { createTimeController } from './core/time-controller.js';
import {
    initStations, setParametro, setSnapshotRows, resumeLive, setFutureHidden,
} from './layers/stations.js';
import { buildPanel } from './ui/panel.js';
import { initSearch } from './ui/search.js';
import { initToolbar } from './ui/toolbar.js';
import { initPerspective } from './ui/perspective.js';
import { initRadar } from './layers/radar.js';
import { initHail } from './layers/hail.js';
import { initSatellite } from './layers/satellite.js';
import { initLightning } from './layers/lightning.js';
import { initStationPopup } from './ui/station-popup.js';
import { initSpiderfy } from './ui/spiderfy.js';
import { initStorms } from './layers/storms.js';
import { initAircraft } from './layers/aircraft.js';
import { initShips } from './layers/ships.js';
import { initSondehub } from './layers/sondehub.js';
import { initIrsSounding } from './layers/irs-sounding.js';
import { initOverlays } from './layers/overlays.js';
import { initMeasure } from './ui/measure.js';
import { initCredits } from './ui/tools.js';
import { initDerivedPoints } from './layers/derived-points.js';
import { initExtremes } from './ui/extremes.js';
import { initLegend } from './ui/legend.js';
import { initNowcastLegend } from './ui/nowcast-legend.js';
import { initAnalysisPoints } from './layers/analysis-points.js';
import { initRiskLayers } from './layers/risk-layers.js';
import { initInterpol } from './layers/interpol.js';
import {initGeographicReferences} from './layers/geographic-references.js';
import { initWslam, getWslamTemporalCoverage, showWslamAtTime } from './layers/wslam.js';
import { initMarineExtra } from './layers/marine-extra.js';
import { initMisc, setInsetsEnabled } from './layers/misc.js';
import { applyDeepLink } from './ui/deeplink.js';
import { initHelp } from './ui/help.js';
import { initPersist } from './ui/persist.js';
import { initTimelineGlass } from './ui/timeline-stack.js';
import { initUnifiedTimeline, isUnifiedEnabled, setUnifiedEnabled, setUnifiedOffsetMinutes, refreshUnifiedCoverage, getUnifiedRangeMinutes, reportForecastAvailability } from './ui/unified-timeline.js';
import { initForecast, setForecastEnabled, showForecastAtTime, prefetchForecast, getForecastTemporalCoverage } from './layers/forecast.js';
import { initDrpcExtra } from './layers/drpc-extra.js';
import { initWaveAnim } from './layers/wave-anim.js';
import { initIntelligence, showIntelligenceAtTime } from './layers/intelligence.js';
import { initGeoportaleSicilia } from './layers/geoportale-sicilia.js';

function currentTheme() {
    try { return localStorage.getItem(STORAGE.theme) === 'dark' ? 'dark' : 'light'; } catch (e) { return 'light'; }
}

// Toast d'errore visibile: se qualcosa esplode sul dispositivo dell'utente,
// il messaggio si LEGGE (serve a diagnosticare ciò che qui non si riproduce).
function initErrorToast() {
    const q = new URLSearchParams(window.location.search);
    if (q.has('embed') || q.has('bot')) return;
    const show = (msg) => {
        try {
            let t = document.querySelector('.wsv2-err-toast');
            if (!t) {
                t = document.createElement('div');
                t.className = 'wsv2-err-toast';
                t.addEventListener('click', () => t.remove());
                document.body.appendChild(t);
            }
            t.textContent = '⚠ ' + String(msg).slice(0, 220);
        } catch (e) { /* mai far danni dal reporter */ }
    };
    window.addEventListener('error', (e) => show(e.message || e.error || 'errore'));
    window.addEventListener('unhandledrejection', (e) => show(e.reason?.message || e.reason || 'promise'));
}

function boot() {
    initErrorToast();
    const url = readUrlState();
    const dark = currentTheme() === 'dark';
    document.documentElement.classList.toggle('ws-theme-dark', dark);

    const view = {};
    if (url.lat != null && url.lon != null) view.center = [url.lon, url.lat];
    if (url.zoom != null) view.zoom = url.zoom;

    const map = createMap('map', view);
    initTimelineGlass(map);
    window.wsv2 = { map }; // handle diagnostico (come window.map in v1)
    const workspaceStore = createWorkspaceStore({ targetMs: Date.now(), temporalMode: 'live', activeScene: 'overview', section: 'overview' });
    const workspaceTime = createTimeController({
        store: workspaceStore,
        liveReference: () => Number(window.WSIntelligenceScenarioContract?.getReceipt?.().coverage?.now_ms) || Date.now(),
    });
    const detachWorkspaceCoverage = workspaceTime.subscribe(event => {
        if (event.type === 'provider-registered' || event.type === 'provider-unregistered') refreshUnifiedCoverage();
    });
    window.WSWorkspace = Object.freeze({
        id: 'ws-weather-workspace/1',
        schemaVersion: 1,
        store: workspaceStore,
        time: workspaceTime,
        getSnapshot: workspaceStore.getState,
        getCoverage: workspaceTime.getCoverage,
    });
    window.wsv2.workspace = window.WSWorkspace;
    const syncWorkspaceTime = (target, mode, origin) => {
        const targetMs = Number(target);
        if (!Number.isFinite(targetMs)) return;
        const request = mode === TEMPORAL_MODES.LIVE
            ? workspaceTime.resetLive({ liveMs: targetMs, origin })
            : workspaceTime.setTarget(targetMs, { mode, origin });
        void Promise.resolve(request).catch(error => console.warn('[WS Workspace time]', error));
    };
    window.addEventListener('pagehide', () => { detachWorkspaceCoverage(); workspaceTime.destroy(); workspaceStore.destroy(); }, { once: true });

    // Un solo popup alla volta, indipendentemente dal modulo che lo ha creato.
    // Il click sul pulsante di chiusura invoca Popup.remove() e libera anche i
    // listener MapLibre; il fallback rimuove tooltip privi di closeButton.
    const closeOpenPopups = () => {
        document.querySelectorAll('.maplibregl-popup').forEach(node => {
            const close = node.querySelector('.maplibregl-popup-close-button');
            if (close) close.click(); else node.remove();
        });
    };
    document.addEventListener('pointerdown', event => {
        if (!event.target.closest?.('.maplibregl-popup')) closeOpenPopups();
    }, true);

    map.on('load', () => {
        // Default v1: vista satellitare ON (chkBaseSat checked in index v1).
        let base = url.base;
        // Un URL con parametri è una vista forzata: non deve ereditare neppure
        // la base OSM/satellitare salvata localmente.
        if (!base && !url.raw.toString()) { try { base = localStorage.getItem(STORAGE.base); } catch (e) {} }
        setBase(map, base === 'osm' ? 'osm' : 'sat');
        applyMapTheme(map, dark);

        for (const [nome, fn] of [
            ['aircraft', () => initAircraft(map)],
            ['ships', () => initShips(map)],
            ['sondehub', () => initSondehub(map)],
            ['irsSounding', () => initIrsSounding(map)],
            ['panel', () => buildPanel(map, url)],
            ['geoportaleSicilia', () => {
                initGeoportaleSicilia(map).catch(err => {
                    console.error('[wsv2] init Geoportale Sicilia fallita:', err);
                });
            }],
            ['search', () => initSearch(map)],
            ['toolbar', () => initToolbar(map)],
            ['perspective', () => initPerspective(map, url)],
            ['radar', () => initRadar(map)],
            ['hail', () => initHail(map)],
            ['satellite', () => initSatellite(map)],
            ['lightning', () => initLightning(map)],
            ['stationPopup', () => initStationPopup(map)],
            ['spiderfy', () => initSpiderfy(map)],
            ['storms', () => initStorms(map)],
            ['overlays', () => initOverlays(map)],
            ['derivedPoints', () => initDerivedPoints(map)],
            ['extremes', () => initExtremes(map)],
            ['legend', () => initLegend()],
            ['nowcastLegend', () => initNowcastLegend()],
            ['analysisPoints', () => initAnalysisPoints(map)],
            ['riskLayers', () => initRiskLayers(map)],
            ['interpol', () => initInterpol(map)],
            ['geographicReferences', () => initGeographicReferences(map)],
            ['kiteSpots', () => initKiteSpots(map)],
            ['wslam', () => initWslam(map)],
            ['marineExtra', () => initMarineExtra(map)],
            ['misc', () => initMisc(map)],
            ['help', () => initHelp()],
            ['unifiedTimeline', () => initUnifiedTimeline(map)],
            ['forecast', () => initForecast(map)],
            ['drpcExtra', () => initDrpcExtra(map)],
            ['waveAnim', () => initWaveAnim(map)],
            ['intelligence', () => initIntelligence(map)],
        ]) {
            try { fn(); } catch (err) {
                console.error('[wsv2] init ' + nome + ' fallita:', err);
                window.dispatchEvent(new ErrorEvent('error', { message: 'init ' + nome + ': ' + (err?.message || err) }));
            }
        }
        // Timeline unificata: passato=snapshot stazioni, futuro=forecast, ora=live.
        let temporalSequence = 0;
        const registerWslamTimeProvider = async () => {
            const coverage = await getWslamTemporalCoverage();
            workspaceTime.registerProvider({
                id: 'wslam',
                toleranceMs: coverage.toleranceMs,
                coverage,
                apply: async (_resolution, context) => {
                    if (!context.isCurrent()) return;
                    await showWslamAtTime(context.targetMs);
                },
            }, {replace: true});
        };
        const showForecastForTime = async (T, sequence) => {
            try {
                const available = await showForecastAtTime(T);
                if (sequence !== temporalSequence) return;
                const enabled = await setForecastEnabled(available);
                if (sequence === temporalSequence) reportForecastAvailability(T, enabled);
            } catch (error) {
                if (sequence !== temporalSequence) return;
                await setForecastEnabled(false);
                reportForecastAvailability(T, false, true);
                console.warn('[wsv2] forecast non disponibile:', error);
            }
        };
        const applyFutureTime = T => {
            const sequence = ++temporalSequence;
            setFutureHidden(true);
            showIntelligenceAtTime(T);
            void showForecastForTime(T, sequence);
        };
        document.addEventListener('wsv2:timeline-snapshot', (e) => {
            syncWorkspaceTime(e.detail?.T, TEMPORAL_MODES.HISTORY, 'unified-timeline');
            temporalSequence += 1;
            setForecastEnabled(false);
            setFutureHidden(false);
            setSnapshotRows(e.detail.rows);
            showIntelligenceAtTime(e.detail.T);
        });
        document.addEventListener('wsv2:timeline-future', (e) => {
            syncWorkspaceTime(e.detail?.T, TEMPORAL_MODES.FUTURE, 'unified-timeline');
            applyFutureTime(e.detail.T);
        });
        // A riposo carica solo le scadenze disponibili, senza competere con
        // le stazioni live. Le località previste si caricano al primo uso futuro.
        if (!document.documentElement.classList.contains('wsv2-embed')) {
            let temporalProvidersStarted = false;
            const startTemporalProviders = () => {
                if (temporalProvidersStarted) return;
                temporalProvidersStarted = true;
                prefetchForecast();
                registerWslamTimeProvider().catch(error => console.warn('[WS Workspace WSLAM coverage]', error));
                getForecastTemporalCoverage().then(coverage => workspaceTime.registerProvider({
                    id: 'local-forecast', toleranceMs: coverage.toleranceMs, coverage,
                }, { replace: true })).catch(error => console.warn('[WS Workspace forecast coverage]', error));
            };
            map.once('idle', () => setTimeout(startTemporalProviders, 1500));
            setTimeout(startTemporalProviders, 3500);
        }
        document.addEventListener('change', event => {
            if (event.target?.id !== 'wslam-product') return;
            setTimeout(() => registerWslamTimeProvider().catch(error => console.warn('[WS Workspace WSLAM product]', error)), 0);
        });
        document.addEventListener('wsv2:timeline-live', (e) => {
            syncWorkspaceTime(e.detail?.T ?? Date.now(), TEMPORAL_MODES.LIVE, 'unified-timeline');
            temporalSequence += 1;
            setForecastEnabled(false);
            setFutureHidden(false);
            resumeLive();
            showIntelligenceAtTime(e.detail?.T ?? Date.now());
        });
        document.addEventListener('wsv2:intelligence-time-request', (e) => {
            const T = Number(e.detail?.T);
            if (!Number.isFinite(T)) return;
            const coverage = window.WSIntelligenceScenarioContract?.getReceipt?.().coverage;
            const nowMs = Number(coverage?.now_ms);
            const isCurrent = Number.isFinite(nowMs) && Math.abs(T - nowMs) < 90 * 1000;
            const offsetMinutes = (T - Date.now()) / 60000;
            const unifiedRange = getUnifiedRangeMinutes();
            if (isUnifiedEnabled() && offsetMinutes >= unifiedRange.min && offsetMinutes <= unifiedRange.max) {
                setUnifiedOffsetMinutes(isCurrent ? 0 : offsetMinutes);
                return;
            }
            if (isCurrent) {
                syncWorkspaceTime(T, TEMPORAL_MODES.LIVE, 'intelligence');
                temporalSequence += 1;
                setForecastEnabled(false);setFutureHidden(false);resumeLive();showIntelligenceAtTime(T);
            } else if (!Number.isFinite(nowMs) || T > nowMs) {
                syncWorkspaceTime(T, TEMPORAL_MODES.FUTURE, 'intelligence');
                applyFutureTime(T);
            } else {
                syncWorkspaceTime(T, TEMPORAL_MODES.HISTORY, 'intelligence');
                temporalSequence += 1;
                setForecastEnabled(false);setFutureHidden(true);showIntelligenceAtTime(T);
            }
        });
        initMeasure(map);
        initCredits();
        // Font Inter pronto PRIMA degli sprite (lezione v1: sprite col font di
        // fallback se costruiti prima di fonts.ready).
        const start = () => {
            initStations(map, url);
            const p = url.raw.get('type') || url.raw.get('parametro');
            if (p) {
                const sel = document.getElementById('selectParametro');
                if (/^rain(?:1|3|6|12|24|48|72|96|120)h$/.test(p))
                    document.dispatchEvent(new CustomEvent('wsv2:rain-window-select', { detail: { value: p } }));
                else if (sel && [...sel.options].some(o => o.value === p)) { sel.value = p; setParametro(p); }
            }
        };
        if (document.fonts?.ready) document.fonts.ready.then(start);
        else start();

        // Deep-link completo (parità v1) + insets default ON come v1
        applyDeepLink(map, url);
        // Persistenza configurazioni tra i reload (dopo il deep-link: l'URL vince)
        initPersist(url);
        // NB: rispetta il toggle "Mostra isole" già ripristinato da deep-link/
        // persist (righe sopra). Prima era incondizionato → dopo un reload le
        // isole tornavano col bottone SPENTO (bug Giuseppe 2026-07-03).
        const intelligenceProduct = document.documentElement.classList.contains('wsi-product');
        const islandInsetsToggle = document.getElementById('chkIslandInsets');
        if (intelligenceProduct) {
            // Non persiste OFF: l'impostazione della mappa Maps resta indipendente.
            // Serve anche ad allineare lo stato accessibile del controllo nascosto.
            if (islandInsetsToggle) islandInsetsToggle.checked = false;
            setInsetsEnabled(false);
        } else if (!document.documentElement.classList.contains('wsv2-embed')
            && islandInsetsToggle?.checked) setInsetsEnabled(true);

        // LAYOUT UNICO (richiesta Giuseppe): stessa shell su smartphone e PC.
        // La fotocamera resta sulla mappa; gli altri strumenti vivono nella
        // griglia in testa al pannello livelli. I listener sopravvivono allo spostamento.
        const toolsRow = document.createElement('div');
        toolsRow.className = 'wsv2-tools-row';
        const panelHeader = document.querySelector('#layersPanel header');
        panelHeader?.after(toolsRow);
        [...document.querySelectorAll('.wsv2-topbar .wsv2-iconbtn')]
            .filter(b => b.id !== 'btnLayers' && b.id !== 'btnMapImage')
            .forEach(b => toolsRow.appendChild(b));

        // Timeline principale: AUTOMATICA, nessun flag (richiesta Giuseppe).
        // Quando il nowcast è attivo (radar/satellite/temporali) si vede SOLO
        // la sua timeline; la principale torna da sola quando lo spegni.
        // E con "Mostra stazioni" OFF la timeline −24/+48 (che mostra proprio
        // stazioni/previsioni) si nasconde, tornando quando le riattivi
        // (bug 2026-07-03). Mai due timeline insieme.
        const utWanted = !document.documentElement.classList.contains('wsv2-embed');
        let nowcastActive = false;
        const stationsShown = () => document.getElementById('chkMarkers')?.checked !== false;
        const syncUt = () => setUnifiedEnabled(utWanted && !nowcastActive && stationsShown());
        document.addEventListener('wsv2:nowcast-active', (e) => { nowcastActive = !!e.detail.active; syncUt(); });
        document.addEventListener('wsv2:stations-toggle', syncUt);
        syncUt();
        const truthyUrl = value => ['1', 'true', 'yes', 'on'].includes(String(value || '').toLowerCase());
        const layerTokens = (url.raw.get('layers') || url.raw.get('only') || url.raw.get('layer') || '')
            .split(',').map(value => value.trim().toLowerCase());
        const forecastRequested = truthyUrl(url.raw.get('forecast')) || truthyUrl(url.raw.get('previsioni'))
            || layerTokens.includes('forecast') || layerTokens.includes('previsioni');
        if (forecastRequested && utWanted) {
            const requestedHours = Number.parseFloat(url.raw.get('forecast_hours') || url.raw.get('ore_previsione') || '1');
            setUnifiedOffsetMinutes((Number.isFinite(requestedHours) ? requestedHours : 1) * 60);
        }

        document.querySelector('.wsv2-boot')?.classList.add('done');
        document.dispatchEvent(new CustomEvent('wsv2:mapready', { detail: { map, url } }));
    });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
